import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Send } from "lucide-react";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { ASSETS, CONTACT } from "@/lib/pnp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Kundenbereich() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!loading && !user) return; // gate below
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
      axios
        .get(`${API}/me/contacts`, { withCredentials: true })
        .then((r) => setInquiries(r.data))
        .catch(() => setInquiries([]));
    }
  }, [user, loading]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    try {
      setSending(true);
      await axios.post(`${API}/contact`, form, { withCredentials: true });
      toast.success("Anfrage gesendet — wir melden uns bald zurück.");
      setForm({ ...form, message: "", phone: "" });
      const r = await axios.get(`${API}/me/contacts`, { withCredentials: true });
      setInquiries(r.data);
    } catch {
      toast.error("Fehler beim Senden. Bitte später erneut versuchen.");
    } finally {
      setSending(false);
    }
  };

  const doLogout = async () => {
    await logout();
    toast.success("Erfolgreich abgemeldet.");
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="h-8 w-8 rounded-full border-2 border-[#082E19] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <main data-testid="kundenbereich-gate" className="min-h-screen bg-white flex flex-col">
        <header className="border-b border-[#DFEBE4] px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full overflow-hidden ring-1 ring-[#DFEBE4]">
              <img src={ASSETS.logo} alt="P&P" className="h-full w-full object-cover" />
            </div>
            <span className="font-display font-bold text-[15px]">P&amp;P Gartenservice</span>
          </Link>
          <Link to="/" className="pp-btn pp-btn-ghost">
            <ArrowLeft size={16} /> Zurück
          </Link>
        </header>

        <section className="flex-1 grid place-items-center px-6">
          <div className="max-w-md w-full text-center">
            <div className="font-mono-pp text-[11px] uppercase tracking-[0.24em] text-[#4F6858] mb-6">
              Kundenbereich · Login
            </div>
            <h1
              className="font-display font-extrabold tracking-tighter leading-[0.9] text-[#082E19]"
              style={{ fontSize: "clamp(2.25rem, 5.5vw, 4rem)" }}
            >
              Willkommen bei P&amp;P.
            </h1>
            <p className="mt-6 text-[#4F6858] leading-relaxed">
              Melden Sie sich mit Ihrem Google-Konto an, um Ihre Anfragen zu sehen
              und schneller neue Aufträge zu erteilen.
            </p>
            <motion.button
              data-testid="google-login-btn"
              whileTap={{ scale: 0.97 }}
              onClick={() => loginWithGoogle("/kundenbereich")}
              className="mt-10 pp-btn pp-btn-primary w-full justify-center"
            >
              <GoogleG />
              Mit Google anmelden
            </motion.button>
            <p className="mt-4 text-[11px] font-mono-pp uppercase tracking-widest text-[#4F6858]">
              Kostenlos · sicher · jederzeit widerrufbar
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="kundenbereich" className="min-h-screen bg-white text-[#082E19]">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#DFEBE4]">
        <div className="flex items-center justify-between px-6 md:px-12 h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full overflow-hidden ring-1 ring-[#DFEBE4]">
              <img src={ASSETS.logo} alt="P&P" className="h-full w-full object-cover" />
            </div>
            <span className="font-display font-bold text-[15px]">P&amp;P Gartenservice</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 pr-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full ring-1 ring-[#DFEBE4]" />
              ) : (
                <span className="h-8 w-8 rounded-full bg-[#17C964] text-[#082E19] font-semibold flex items-center justify-center text-sm">
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </span>
              )}
              <span data-testid="user-name" className="font-mono-pp text-[11px] uppercase tracking-[0.2em] text-[#082E19]">
                {user.name}
              </span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="pp-btn pp-btn-ghost"
            >
              <LogOut size={15} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <section className="px-6 md:px-12 pt-16 md:pt-24">
        <div className="font-mono-pp text-[11px] uppercase tracking-[0.24em] text-[#4F6858] mb-6">
          Kundenbereich
        </div>
        <h1
          className="font-display font-extrabold tracking-tighter leading-[0.9]"
          style={{ fontSize: "clamp(2.5rem, 6.5vw, 5rem)" }}
        >
          Hallo {user.name?.split(" ")[0] || "Kunde"}.
        </h1>
        <p className="mt-6 text-[#4F6858] max-w-xl leading-relaxed">
          Hier finden Sie alle Ihre Anfragen an P&amp;P Gartenservice und können
          direkt eine neue senden.
        </p>
      </section>

      <section className="px-6 md:px-12 mt-16 md:mt-20 grid grid-cols-12 gap-8 md:gap-10 pb-24 md:pb-32">
        {/* New inquiry */}
        <div className="col-span-12 lg:col-span-5">
          <div className="rounded-md ring-1 ring-[#DFEBE4] p-6 md:p-8">
            <div className="font-mono-pp text-[11px] uppercase tracking-[0.22em] text-[#4F6858]">
              Neue Anfrage · Direkt an uns
            </div>
            <form onSubmit={onSubmit} data-testid="new-inquiry-form" className="mt-4 space-y-5">
              <input
                data-testid="inq-name"
                className="pp-input"
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <input
                data-testid="inq-email"
                className="pp-input"
                type="email"
                placeholder="E-Mail"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <input
                data-testid="inq-phone"
                className="pp-input"
                placeholder="Telefon (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <textarea
                data-testid="inq-message"
                className="pp-input resize-none"
                rows={4}
                placeholder="Was braucht Ihr Garten?"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
              />
              <motion.button
                data-testid="inq-submit"
                whileTap={{ scale: 0.97 }}
                type="submit"
                disabled={sending}
                className="pp-btn pp-btn-primary w-full justify-center disabled:opacity-60"
              >
                {sending ? "Wird gesendet…" : (<><Send size={15} /> Anfrage senden</>)}
              </motion.button>
            </form>
            <div className="mt-6 pt-6 border-t border-[#DFEBE4] text-[11px] font-mono-pp uppercase tracking-widest text-[#4F6858]">
              Oder direkt · <a href={CONTACT.telHref} className="hover:text-[#082E19]">Anrufen</a>
              {" · "}
              <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="hover:text-[#082E19]">WhatsApp</a>
            </div>
          </div>
        </div>

        {/* Inquiries list */}
        <div className="col-span-12 lg:col-span-7">
          <div className="flex items-end justify-between mb-6">
            <h2
              className="font-display font-bold tracking-tight"
              style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)" }}
            >
              Meine Anfragen
            </h2>
            <span className="font-mono-pp text-[11px] uppercase tracking-[0.22em] text-[#4F6858]">
              {inquiries === null ? "…" : `${inquiries.length} Eintrag${inquiries.length === 1 ? "" : "e"}`}
            </span>
          </div>

          {inquiries === null && (
            <div className="h-24 grid place-items-center">
              <div className="h-6 w-6 rounded-full border-2 border-[#082E19] border-t-transparent animate-spin" />
            </div>
          )}

          {inquiries !== null && inquiries.length === 0 && (
            <div
              data-testid="inquiries-empty"
              className="rounded-md ring-1 ring-[#DFEBE4] p-8 text-center text-[#4F6858]"
            >
              Noch keine Anfragen. Senden Sie uns links Ihre erste 🌱
            </div>
          )}

          {inquiries && inquiries.length > 0 && (
            <ul data-testid="inquiries-list" className="divide-y divide-[#DFEBE4] border-y border-[#DFEBE4]">
              {inquiries.map((it) => (
                <li key={it.id} className="py-5 grid grid-cols-12 gap-4">
                  <div className="col-span-3 md:col-span-2 font-mono-pp text-[11px] uppercase tracking-[0.2em] text-[#4F6858]">
                    {new Date(it.created_at).toLocaleDateString("de-DE", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                  <div className="col-span-9 md:col-span-10">
                    <div className="text-[#082E19] leading-relaxed">{it.message}</div>
                    {it.phone && (
                      <div className="mt-1 text-[12px] text-[#4F6858]">Tel: {it.phone}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-6 7.7-11.3 7.7-6.8 0-12.4-5.5-12.4-12.4S17.2 10.9 24 10.9c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 4.9 29.3 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.9 29.3 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5.2 0 10-1.9 13.6-5.2l-6.3-5.2c-2 1.4-4.5 2.3-7.3 2.3-5.3 0-9.7-3.2-11.3-7.7l-6.5 5C9.6 40.7 16.2 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-0.8 2.2-2.2 4-4 5.3l6.3 5.2C41.9 34.5 45 29.7 45 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
